import { withErrorBoundary } from "@sentry/nextjs";
import { gql } from "graphql-request";
import { useKeenSlider } from "keen-slider/react";
import { useCallback, useEffect, useState, VFC } from "react";
import styled from "styled-components";
import { Markdown } from "components";
import { useWindowSize, useAnchor } from "hooks";
import { theme } from "styles";
import { Carousel4Content } from "types/graphql/generated";
import { Card, Carousel4Button } from "./components";

interface Props {
  content: Carousel4Content;
}

const Carousel4: VFC<Props> = withErrorBoundary(({ content: { title, description, carousel4Teams } }) => {
  const [sliderRef, instanceRef] = useKeenSlider({
    slides: {
      perView: 3.28,
      spacing: 32,
    },
    breakpoints: {
      [`(max-width: ${theme.breakpoints.lg}px)`]: {
        slides: {
          perView: 2.3,
          spacing: 32,
        },
      },
      [`(max-width: ${theme.breakpoints.md}px)`]: {
        slides: {
          perView: 1.2,
          spacing: 16,
        },
      },
      [`(max-width: ${theme.breakpoints.sm}px)`]: {
        slides: {
          perView: 1.05,
          spacing: 16,
        },
      },
    },
  });

  const [animationStage, setAnimationStage] = useState("");

  instanceRef.current?.on("animationEnded", () => {
    setAnimationStage("animationEnded");
  });

  instanceRef.current?.on("animationStarted", () => {
    setAnimationStage("animationStarted");
  });

  const windowSize = useWindowSize();

  useEffect(() => {
    const sliderCards = document.querySelectorAll(".Carousel4__carouselItem");

    sliderCards.forEach((card) => {
      const { x: cardStart, right: cardEnd } = card.getBoundingClientRect();

      if (window.innerWidth < cardEnd || cardStart < 0) {
        card.classList.add("fade");
      } else {
        card.classList.remove("fade");
      }
    });
  }, [windowSize, animationStage]);

  useAnchor("carousel4");

  const handleClickPrevButton = useCallback(() => instanceRef.current?.prev(), [instanceRef]);
  const handleClickNextButton = useCallback(() => instanceRef.current?.next(), [instanceRef]);

  return (
    <StyledCarousel4 className="carousel4">
      <div className="Carousel4 Content">
        <h2 className="Font_48_54_600 md:Font_40_48_600">{title}</h2>
        <Markdown className="Carousel4__description Color_secondary Font_24_32_450">{description}</Markdown>
        <div className="Carousel4__carousel keen-slider" ref={sliderRef}>
          {carousel4Teams.map((team) => (
            <div key={team.id} className="Carousel4__carouselItem keen-slider__slide">
              <Card content={team} />
            </div>
          ))}
        </div>
        <div className="Carousel3__arrowsContainerBottom">
          <Carousel4Button
            className="Carousel3__arrowLeft_bottom"
            onClick={handleClickPrevButton}
            aria-label="button-left"
          />
          <Carousel4Button
            className="Carousel3__arrowRight_bottom"
            onClick={handleClickNextButton}
            aria-label="button-right"
            direction="right"
          />
        </div>
      </div>
    </StyledCarousel4>
  );
}, {});

const StyledCarousel4 = styled.section`
  overflow: hidden;
  margin-top: -64px;
  padding-top: 64px;

  .fade {
    opacity: 0.4;
  }

  .Carousel4__description {
    margin-top: 24px;
    max-width: 765px;
  }

  .Carousel4__carousel {
    display: flex;
    margin-top: 70px;
    overflow: visible;
  }

  .Carousel3__arrowsContainerBottom {
    display: flex;
    justify-content: flex-end;
    margin-top: 24px;
    padding-right: 106px;
  }

  .Carousel3__arrowLeft_bottom,
  .Carousel3__arrowRight_bottom {
    min-width: 48px;
    height: 48px;
  }

  .Carousel3__arrowRight_bottom {
    margin-left: 24px;
    transform: rotate(180deg);
  }

  @media (max-width: ${({ theme }) => `${theme.breakpoints.lg}px`}) {
    .Carousel3__arrowsContainerBottom {
      padding-right: unset;
    }

    .Carousel4__carousel {
      margin-top: 64px;
    }
  }

  @media (max-width: ${({ theme }) => `${theme.breakpoints.md}px`}) {
    .Carousel4__carousel {
      margin-top: 48px;
    }
  }
`;

gql`
  fragment Carousel4Content on ComponentCarouselCarousel4 {
    title
    description
    carousel4Teams {
      ...Carousel4CardContent
    }
  }
`;

export { Carousel4 };
export type { Props as Carousel4Props };
